import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import type { FunctionComponent } from "react";

import Button from "~/components/buttons/Button/Button";
import Card from "~/components/Card/Card";
import Headline from "~/components/Headline/Headline";
import Hint from "~/components/Hint";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import Modal from "~/components/Modal";
import { eInvoiceQueries } from "~/modules/billing/api/eInvoice/eInvoiceQueries.ts";
import { useEInvoiceDownload } from "~/modules/billing/hooks/useEInvoiceDownload.ts";

import EInvoiceBillingPeriodSection from "./components/EInvoiceBillingPeriodSection.tsx";
import EInvoiceBuyerSection from "./components/EInvoiceBuyerSection.tsx";
import EInvoiceDocumentSection from "./components/EInvoiceDocumentSection.tsx";
import EInvoiceLineItemSection from "./components/EInvoiceLineItemSection.tsx";
import EInvoicePaymentSection from "./components/EInvoicePaymentSection.tsx";
import EInvoiceSectionErrors from "./components/EInvoiceSectionErrors.tsx";
import EInvoiceSellerSection from "./components/EInvoiceSellerSection.tsx";
import EInvoiceTotalsSection from "./components/EInvoiceTotalsSection.tsx";

type Props = { invoiceId: string; onClose: () => void; isOpen: boolean };

const EInvoicePreviewModal: FunctionComponent<Props> = ({ invoiceId, isOpen, onClose }) => {
	const { data, isLoading, isError } = useQuery(eInvoiceQueries.preview(invoiceId, isOpen));
	const { isGenerating, handleDownload } = useEInvoiceDownload(invoiceId);
	const hasErrors = data?.errors ? Object.keys(data.errors).length > 0 : false;
	return (
		<Modal open={isOpen}
			   setOpen={onClose}
			   theme="dark"
			   widthClassName="w-[90rem]">
			<div className="mb-4 flex items-center justify-between">
				<Headline type="h4">E-Rechnung Vorschau</Headline>
				<div className="flex items-center">
					<Button theme="primary"
							size="auto"
							className="px-3 py-1"
							disabled={isGenerating || isLoading || hasErrors}
							onClick={handleDownload}>
						<div className="flex items-center gap-x-2">
							<span>E-Rechnung</span>
							{!isGenerating ? <ArrowDownTrayIcon className="size-4" /> : <LoadingSpinner theme="neutral"
																										scale={0.5} />}
						</div>
					</Button>
					<Button theme="none"
							onClick={onClose}>Schließen</Button>
				</div>
			</div>
			{isLoading && <span className="text-sm text-gray-500">Vorschau wird geladen …</span>}
			{isError && <Hint theme="error">Vorschau konnte nicht geladen werden.</Hint>}

			{data !== undefined && (
				<div className="grid grid-cols-2 gap-8">
					<Card title="Rechnung">
						<div className="flex flex-col gap-4">
							<EInvoiceSectionErrors errors={data.errors.invoice} />
							<div className="grid grid-cols-2 gap-6">
								<EInvoiceDocumentSection document={data.document} />
								<EInvoiceTotalsSection totals={data.totals} />
								<EInvoiceBillingPeriodSection billingPeriod={data.billingPeriod} />
								<EInvoicePaymentSection payment={data.payment} />
								<EInvoiceLineItemSection lineItem={data.lineItem} />
							</div>
						</div>
					</Card>

					<div className="flex flex-col gap-8">
						<EInvoiceBuyerSection buyer={data.buyer}
											  errors={data.errors.buyer} />
						<EInvoiceSellerSection seller={data.seller}
											   errors={data.errors.seller} />
					</div>
				</div>
			)}
		</Modal>
	);
};

export default EInvoicePreviewModal;
